import React, { useState, useEffect } from 'react';
import { 
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  AccountBalance as AccountingIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  Assessment as ReportsIcon,
  InsertChart as StatisticsIcon,
  Group as CustomersIcon,
  LocalAtm as FinanceIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Description as DocumentsIcon,
  Email as EmailIcon,
  People as StaffIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Campaign as MarketingIcon,
  FormatListBulleted as ListIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import MainAdminLayout from './MainAdminLayout';
import { syncAllSlipStatuses } from '../firebase/services/slipService';

interface BusinessAdminLayoutProps {
  children: React.ReactNode;
}

const BusinessAdminLayout: React.FC<BusinessAdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  
  // State for expandable menu sections
  const [reportsOpen, setReportsOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);

  // Run slip status sync when admin is accessed
  useEffect(() => {
    const runSlipSync = async () => {
      try {
        console.log('🔄 Running automatic slip status sync...');
        const results = await syncAllSlipStatuses();
        
        if (results.updated > 0) {
          console.log(`✅ Slip sync complete: Updated ${results.updated} of ${results.total} slips`);
          results.changes.forEach(change => {
            console.log(`  • Slip ${change.slipNumber}: ${change.from} → ${change.to}`);
          });
        } else {
          console.log('✅ All slip statuses were already correct');
        }
      } catch (error) {
        console.warn('⚠️ Slip sync failed (non-critical):', error);
      }
    };

    // Run sync in background without blocking UI
    runSlipSync();
  }, []); // Run once when layout mounts

  // Function to check if a path is active
  const isActive = (path: string) => {
    return location.pathname === path || 
           (path !== '/admin/business' && location.pathname.startsWith(path));
  };

  // ListItemLink component for navigation links
  const ListItemLink = ({ icon, primary, to }: { icon: React.ReactElement, primary: string, to: string }) => {
    const active = isActive(to);
    
    return (
      <ListItem 
        button 
        component={Link} 
        to={to}
        sx={{
          backgroundColor: active ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          borderLeft: active ? '4px solid' : '4px solid transparent',
          borderLeftColor: active ? 'primary.main' : 'transparent',
          pl: active ? 1.75 : 2,
        }}
      >
        <ListItemIcon sx={{ color: active ? 'primary.main' : 'inherit' }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={primary} 
          primaryTypographyProps={{ 
            fontWeight: active ? 'bold' : 'regular',
            color: active ? 'primary' : 'inherit'
          }}
        />
      </ListItem>
    );
  };

  const businessNavigation = (
    <List component="nav">
      <ListItemLink
        to="/admin/business"
        primary="Dashboard"
        icon={<DashboardIcon />}
      />
      
      {/* Customer Management */}
      <ListItemLink
        to="/admin/business/customers"
        primary="Customer Management"
        icon={<CustomersIcon />}
      />
      
      {/* Marketing Section */}
      <ListItem button onClick={() => setMarketingOpen(!marketingOpen)}>
        <ListItemIcon>
          <MarketingIcon />
        </ListItemIcon>
        <ListItemText primary="Marketing" />
        {marketingOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={marketingOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/admin/business/marketing/campaigns"
            primary="Email Campaigns"
            icon={<EmailIcon />}
          />
          <ListItemLink
            to="/admin/business/marketing/contacts"
            primary="Contacts"
            icon={<PeopleIcon />}
          />
          <ListItemLink
            to="/admin/business/marketing/lists"
            primary="Contact Lists"
            icon={<ListIcon />}
          />
          <ListItemLink
            to="/admin/business/marketing/settings"
            primary="Marketing Settings"
            icon={<SettingsIcon />}
          />
        </List>
      </Collapse>
      
      {/* Staff Management Section */}
      <ListItem button onClick={() => setStaffOpen(!staffOpen)}>
        <ListItemIcon>
          <StaffIcon />
        </ListItemIcon>
        <ListItemText primary="Staff Management" />
        {staffOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={staffOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/admin/business/staff"
            primary="Staff Directory"
            icon={<StaffIcon />}
          />
          <ListItemLink
            to="/admin/business/staff/scheduling"
            primary="Staff Scheduling"
            icon={<ScheduleIcon />}
          />
          <ListItemLink
            to="/admin/business/staff/roles"
            primary="Role Management"
            icon={<SecurityIcon />}
          />
        </List>
      </Collapse>
      
      {/* Business Locations */}
      <ListItemLink
        to="/admin/business/locations"
        primary="Business Locations"
        icon={<BusinessIcon />}
      />
      
      {/* Reports Section */}
      <ListItem button onClick={() => setReportsOpen(!reportsOpen)}>
        <ListItemIcon>
          <ReportsIcon />
        </ListItemIcon>
        <ListItemText primary="Reports & Analytics" />
        {reportsOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/admin/business/reports/overview"
            primary="Business Overview"
            icon={<StatisticsIcon />}
          />
          <ListItemLink
            to="/admin/business/reports/marina"
            primary="Marina Reports"
            icon={<StatisticsIcon />}
          />
          <ListItemLink
            to="/admin/business/reports/store"
            primary="Store Reports"
            icon={<StatisticsIcon />}
          />
          <ListItemLink
            to="/admin/business/reports/kitchen"
            primary="Kitchen Reports"
            icon={<StatisticsIcon />}
          />
        </List>
      </Collapse>
      
      {/* Finance Section */}
      <ListItem button onClick={() => setFinanceOpen(!financeOpen)}>
        <ListItemIcon>
          <FinanceIcon />
        </ListItemIcon>
        <ListItemText primary="Finance" />
        {financeOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={financeOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/admin/business/finance/overview"
            primary="Financial Overview"
            icon={<FinanceIcon />}
          />
          <ListItemLink
            to="/admin/business/finance/accounting"
            primary="Accounting"
            icon={<AccountingIcon />}
          />
          <ListItemLink
            to="/admin/business/finance/transactions"
            primary="Transactions"
            icon={<FinanceIcon />}
          />
        </List>
      </Collapse>
      
      {/* Documents */}
      <ListItemLink
        to="/admin/business/documents"
        primary="Business Documents"
        icon={<DocumentsIcon />}
      />
      
      {/* Notifications */}
      <ListItemLink
        to="/admin/business/notifications"
        primary="Notifications"
        icon={<NotificationsIcon />}
      />
      
      {/* Settings Section */}
      <ListItem button onClick={() => setSettingsOpen(!settingsOpen)}>
        <ListItemIcon>
          <SettingsIcon />
        </ListItemIcon>
        <ListItemText primary="Settings" />
        {settingsOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/admin/business/settings/general"
            primary="General Settings"
            icon={<SettingsIcon />}
          />
          <ListItemLink
            to="/admin/business/settings/security"
            primary="Security"
            icon={<SecurityIcon />}
          />
          <ListItemLink
            to="/admin/business/settings/email"
            primary="Email Settings"
            icon={<EmailIcon />}
          />
          <ListItemLink
            to="/admin/business/settings/images"
            primary="Image Management"
            icon={<PhotoLibraryIcon />}
          />
        </List>
      </Collapse>
    </List>
  );

  return (
    <MainAdminLayout drawerContent={businessNavigation}>
      {children}
    </MainAdminLayout>
  );
};

export default BusinessAdminLayout;

 